import DeepFocusClock from "@/assets/icons/deep-focus-clock.svg";
import { Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "../ui/input";
import DeepFocusTooltip from "./DeepFocusTooltip";

const DeepFocusTimer = () => {
    const { t } = useTranslation();
    const [minutes, setMinutes] = useState(0);
    const [seconds, setSeconds] = useState(0);
    const [isRunning, setIsRunning] = useState(false);
    const [displayTime, setDisplayTime] = useState("00:00");
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
        let interval: ReturnType<typeof setInterval>;

        if (isRunning) {
            interval = setInterval(() => {
                if (seconds > 0) {
                    setSeconds((prev) => prev - 1);
                } else if (minutes > 0) {
                    setMinutes((prev) => prev - 1);
                    setSeconds(59);
                } else {
                    setIsRunning(false);
                    setIsComplete(true);
                }
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [isRunning, minutes, seconds]);

    useEffect(() => {
        const formattedMinutes = minutes.toString().padStart(2, "0");
        const formattedSeconds = seconds.toString().padStart(2, "0");
        setDisplayTime(
            t("deepFocus.timer.timeFormat", {
                minutes: formattedMinutes,
                seconds: formattedSeconds,
            }),
        );
    }, [minutes, seconds, t]);

    const handleStartStop = () => {
        if (minutes === 0 && seconds === 0) return;
        setIsRunning(!isRunning);
        if (isComplete) {
            setIsComplete(false);
        }
    };

    return (
        <DeepFocusTooltip
            action={
                <p className="flex items-center gap-2">
                    {isRunning
                        ? t("deepFocus.timer.stop")
                        : t("deepFocus.timer.start")}
                </p>
            }
            content={
                <div className="flex items-center">
                    <Clock className="size-6 pr-2" />
                    <Input
                        aria-label={t("deepFocus.timer.minutes")}
                        className="w-7 px-2 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        disabled={isRunning}
                        max={99}
                        min={0}
                        onChange={(e) => setMinutes(Number(e.target.value))}
                        type="number"
                        value={minutes}
                        variant="ghost"
                    />
                    <span>:</span>
                    <Input
                        aria-label={t("deepFocus.timer.seconds")}
                        className="w-7 px-2 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        disabled={isRunning}
                        max={59}
                        min={0}
                        onChange={(e) => setSeconds(Number(e.target.value))}
                        type="number"
                        value={seconds}
                        variant="ghost"
                    />
                </div>
            }
            handleAction={handleStartStop}
        >
            <img src={DeepFocusClock} alt="Clock" className="size-4" />
            <p className={isComplete ? "text-red-500" : ""}>{displayTime}</p>
        </DeepFocusTooltip>
    );
};

export default DeepFocusTimer;
